import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ApplicationName, POLICIES } from 'Config';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMapLocation, faChevronLeft, faChevronDown, faChevronRight } from '@fortawesome/free-solid-svg-icons';

export interface SideBarItem {
    text: string;
    url?: string;
    icon?: any;
    policies: (keyof typeof POLICIES)[];
    type?: 'OR' | 'AND';
    items?: SideBarItem[];
}

type Props = {
    items: SideBarItem[];
    mobileOpen?: boolean;
    onMobileClose?: () => void;
};

const SideBar: React.FunctionComponent<Props> = ({ items, mobileOpen, onMobileClose }: Props) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [collapsed, setCollapsed] = useState(false);
    const [expandedGroups, setExpandedGroups] = useState<Record<number, boolean>>({});

    const handleSelect = (url: string | undefined) => {
        if (!url || url === '/#') return;
        navigate(url);
        if (onMobileClose) {
            onMobileClose();
        }
    };

    const toggleGroup = (index: number) => {
        setExpandedGroups(prev => ({ ...prev, [index]: !prev[index] }));
    };

    const isActive = (url?: string) => {
        if (!url || url === '/#') return false;
        return location.pathname === url || location.pathname.startsWith(url + '/');
    };

    const isGroupActive = (item: SideBarItem) => {
        if (isActive(item.url)) return true;
        return item.items?.some(sub => isActive(sub.url)) ?? false;
    };

    const sidebarWidth = collapsed ? 'w-sidebar-collapsed' : 'w-sidebar-expanded';

    return (
        <>
            {mobileOpen && (
                <div
                    className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[1040] sm:hidden"
                    onClick={onMobileClose}
                />
            )}

            <aside
                className={`
                    bg-slate-900 border-r border-white/5 flex flex-col transition-all duration-250 ease-in-out z-[1050]
                    ${sidebarWidth}
                    hidden sm:flex
                    ${mobileOpen ? '!flex !fixed inset-y-0 left-0 !w-sidebar-expanded shadow-lg' : ''}
                `}
                style={{ gridArea: 'sidebar' }}
            >
                {/* Brand */}
                <div className="flex items-center h-navbar-height px-4 border-b border-white/5 shrink-0">
                    {!collapsed
                        ? (
                            <div className="flex items-center gap-3 overflow-hidden">
                                <div className="w-8 h-8 rounded-xl bg-white/10 border border-white/20 flex items-center justify-center shrink-0">
                                    <FontAwesomeIcon icon={faMapLocation} className="text-white text-xs" />
                                </div>
                                <span className="text-white font-semibold text-sm tracking-wide whitespace-nowrap">{ApplicationName}</span>
                            </div>
                        )
                        : (
                            <div className="w-full flex justify-center">
                                <div className="w-8 h-8 rounded-xl bg-white/10 border border-white/20 flex items-center justify-center">
                                    <FontAwesomeIcon icon={faMapLocation} className="text-white text-xs" />
                                </div>
                            </div>
                        )
                    }
                </div>

                {/* Nav */}
                <nav className="flex-1 overflow-y-auto py-2">
                    {items.map((item, index) => (
                        item.items && item.items.length > 0
                            ? renderGroup(item, index)
                            : renderItem(item, index)
                    ))}
                </nav>

                {/* Collapse toggle */}
                <div className={`shrink-0 border-t border-white/5 p-2 ${mobileOpen ? 'hidden' : 'hidden sm:block'}`}>
                    <button
                        onClick={() => setCollapsed(prev => !prev)}
                        className="w-full flex items-center justify-center py-2 text-slate-600 hover:text-slate-300 transition-colors border-0 bg-transparent"
                    >
                        <FontAwesomeIcon icon={collapsed ? faChevronRight : faChevronLeft} className="text-xs" />
                    </button>
                </div>
            </aside>
        </>
    );

    function renderItem(item: SideBarItem, index: number) {
        const active = isActive(item.url);
        return (
            <button
                key={`item_${index}`}
                onClick={() => handleSelect(item.url)}
                className={`
                    w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left transition-all duration-150 cursor-pointer border-0 bg-transparent
                    border-l-2
                    ${collapsed ? 'justify-center px-0' : ''}
                    ${active
                        ? 'border-l-white bg-white/10 text-white font-medium'
                        : 'border-l-transparent text-slate-400 hover:text-slate-100 hover:bg-white/[0.04]'
                    }
                `}
                title={collapsed ? item.text : undefined}
            >
                {item.icon && (
                    <span className="w-5 text-center shrink-0">
                        <FontAwesomeIcon icon={item.icon} className="text-[15px]" />
                    </span>
                )}
                {!collapsed && (
                    <span className="whitespace-nowrap overflow-hidden text-ellipsis">{item.text}</span>
                )}
            </button>
        );
    }

    function renderGroup(item: SideBarItem, index: number) {
        const isOpen = expandedGroups[index] || isGroupActive(item);

        return (
            <div key={`group_${index}`}>
                {/* Section label */}
                {!collapsed && (
                    <button
                        onClick={() => toggleGroup(index)}
                        className="w-full flex items-center justify-between px-4 py-2 mt-2 border-0 bg-transparent cursor-pointer"
                    >
                        <span className="text-[10px] font-semibold tracking-widest uppercase text-slate-600">
                            {item.text}
                        </span>
                        <FontAwesomeIcon
                            icon={faChevronDown}
                            className={`text-[9px] text-slate-600 transition-transform duration-200 ${isOpen ? '' : '-rotate-90'}`}
                        />
                    </button>
                )}

                {/* Sub-items */}
                {!collapsed && (
                    <div className={`overflow-hidden transition-all duration-200 ease-in-out ${isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
                        {item.items?.map((subitem, subIndex) => {
                            const subActive = isActive(subitem.url);
                            return (
                                <button
                                    key={`subitem_${index}_${subIndex}`}
                                    onClick={() => handleSelect(subitem.url)}
                                    className={`
                                        w-full flex items-center gap-3 px-5 py-2.5 text-sm text-left transition-all duration-150 cursor-pointer border-0 bg-transparent
                                        border-l-2
                                        ${subActive
                                            ? 'border-l-white bg-white/10 text-white font-medium'
                                            : 'border-l-transparent text-slate-400 hover:text-slate-100 hover:bg-white/[0.04]'
                                        }
                                    `}
                                >
                                    {subitem.icon && (
                                        <span className="w-5 text-center shrink-0">
                                            <FontAwesomeIcon icon={subitem.icon} className="text-[15px]" />
                                        </span>
                                    )}
                                    <span className="whitespace-nowrap overflow-hidden text-ellipsis">{subitem.text}</span>
                                </button>
                            );
                        })}
                    </div>
                )}

                {/* Collapsed: just show icon for the group */}
                {collapsed && item.icon && (
                    <button
                        onClick={() => { setCollapsed(false); toggleGroup(index); }}
                        className="w-full flex items-center justify-center py-2.5 text-slate-400 hover:text-slate-100 hover:bg-white/[0.04] transition-all duration-150 cursor-pointer border-0 bg-transparent"
                        title={item.text}
                    >
                        <FontAwesomeIcon icon={item.icon} className="text-[15px]" />
                    </button>
                )}
            </div>
        );
    }
};

export default SideBar;
